import { Outlet, createRootRoute, useRouterState } from '@tanstack/react-router'
import { useState } from 'react'
import { Stack } from '@astryxdesign/core/Stack'
import { Section } from '@astryxdesign/core/Section'
import { Text } from '@astryxdesign/core/Text'
import { Badge } from '@astryxdesign/core/Badge'
import { Icon } from '@astryxdesign/core/Icon'
import { IconButton } from '@astryxdesign/core/IconButton'
import { MobileNav } from '@astryxdesign/core/MobileNav'
import { SideNavItem, SideNavSection } from '@astryxdesign/core/SideNav'
import { TabList, Tab } from '@astryxdesign/core/TabList'
import { useAuth, useSyncState, useTrip } from '#/lib/hooks'
import { currencySymbol } from '#/lib/money'
import { Logo } from '#/components/Logo'
import { PullToRefresh } from '#/components/PullToRefresh'
import { SignedOut } from '#/components/SignedOut'
import { isAdmin } from '#/lib/types'
import { AddExpenseSheet } from '#/components/AddExpenseSheet'

/*
  Four tabs with the add-expense action raised between them.

  The bar is two tab strips rather than one: a four-across strip has no middle,
  and dropping a raised button on top of it would cover the inner edge of two
  tabs. Two strips of two leave a real gap for the action to sit in. They share
  one value, and a strip whose value matches none of its tabs simply shows none
  selected — which is what already happens on the manage screens.
*/

const LEFT_TABS = [
  { to: '/', label: 'Today' },
  { to: '/trip', label: 'Trip' },
]
const RIGHT_TABS = [
  { to: '/spend', label: 'Spend' },
  { to: '/log', label: 'Log' },
]
const TABS = [...LEFT_TABS, ...RIGHT_TABS]

export const Route = createRootRoute({ component: Shell })

/**
 * Behind the burger. Admin-only sections are not rendered for a co-traveller.
 *
 * Links is not here: a share link is a way into the trip for a particular
 * person, so it belongs beside that person in People rather than in a list of
 * URLs with no faces on it.
 */
const MENU = [
  { to: '/manage/people', label: 'People', adminOnly: true },
  { to: '/manage/documents', label: 'Documents', adminOnly: false },
  { to: '/manage/itinerary', label: 'Itinerary', adminOnly: true },
]

/** Shared with everyone: a category someone adds shows up on every phone. */
const MONEY_MENU = [
  { to: '/manage/categories', label: 'Categories', adminOnly: false },
]

/** Not shared. Text size and colour mode belong to the phone holding them. */
const DEVICE_MENU = [
  { to: '/manage/accessibility', label: 'Accessibility', adminOnly: false },
]

function Shell() {
  const [adding, setAdding] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const path = useRouterState({ select: (s) => s.location.pathname })
  const navigate = Route.useNavigate()
  const { me, state } = useAuth()
  const sync = useSyncState()
  const trip = useTrip()

  // Empty when no tab owns the route (Settings), so nothing is falsely lit.
  // No credential: show the two ways in rather than an app with nothing in it.
  if (state === 'out') return <SignedOut />

  const current = TABS.find((t) => (t.to === '/' ? path === '/' : path.startsWith(t.to)))?.to ?? ''

  const pill =
    sync.status === 'offline' ? { text: 'Offline', variant: 'neutral' as const }
    : sync.pending > 0 ? { text: `${sync.pending} unsynced`, variant: 'orange' as const }
    : sync.status === 'reauth' ? { text: 'Sign in again', variant: 'red' as const }
    : sync.status === 'error' ? { text: 'Sync failed', variant: 'orange' as const }
    : null

  return (
    <Stack direction="vertical" height="100dvh" gap={0}>
      <div className="app-header">
      <Section padding={3} dividers={['bottom']}>
        <Stack direction="horizontal" justify="between" align="center" gap={2}>
          <Stack direction="horizontal" align="center" gap={2}>
            {/* The burger leads the row, so it is under the thumb that opens
                it and the drawer comes from the edge it sits on. */}
            <div className="app-burger">
              <IconButton
                label="Menu"
                size="lg"
                variant="ghost"
                icon={<Icon icon="menu" size="lg" />}
                onClick={() => setMenuOpen(true)}
              />
            </div>
            <Logo size={30} />
            <Text type="label" weight="semibold">Travel</Text>
          </Stack>
          {/* Silent queues are how people stop trusting an app. */}
          {pill && <Badge label={pill.text} variant={pill.variant} />}
        </Stack>
      </Section>
      </div>

      {/* PullToRefresh owns the scroll container now — it has to, to tell a
          pull at the top from an ordinary scroll. It carries the same 16px
          padding the Stack had, which the sticky offsets are measured from. */}
      <PullToRefresh>
        <Outlet />
      </PullToRefresh>

      {/* .app-footer carries the safe-area inset and the 48px touch floor —
          both device concerns with no component prop. */}
      <div className="app-footer">
      <Section padding={3} dividers={['top']}>
        <Stack direction="horizontal" align="center" gap={2}>
          <div className="app-tabgroup">
            <TabList value={current} onChange={(v) => navigate({ to: v })} layout="fill">
              {LEFT_TABS.map((t) => <Tab key={t.to} value={t.to} label={t.label} />)}
            </TabList>
          </div>

          {/* Raised, so the one thing done twenty times a day is the one thing
              you cannot miss. elevation is IconButton's own FAB affordance; the
              lift out of the bar is the only part that needs CSS.

              A currency glyph, not a plus. The button does one thing wherever
              you are, and a plus sitting over People or Documents read as "add
              a person" or "add a document". The symbol is the trip's own, from
              the same formatter that prints every amount, so the button says
              the same thing as the rows it creates. */}
          <div className="app-fab">
            <IconButton
              label="Add expense"
              variant="primary"
              size="lg"
              elevation="high"
              icon={<Text type="inherit">{currencySymbol(trip?.base_currency)}</Text>}
              onClick={() => setAdding(true)}
            />
          </div>

          <div className="app-tabgroup">
            <TabList value={current} onChange={(v) => navigate({ to: v })} layout="fill">
              {RIGHT_TABS.map((t) => <Tab key={t.to} value={t.to} label={t.label} />)}
            </TabList>
          </div>
        </Stack>
      </Section>
      </div>

      <MobileNav
        isOpen={menuOpen}
        onOpenChange={setMenuOpen}
        side="start"
        header="Manage"
      >
        <SideNavSection title="Trip">
          {MENU.filter((m) => !m.adminOnly || isAdmin(me?.role)).map((m) => (
            <SideNavItem
              key={m.to}
              label={m.label}
              isSelected={path.startsWith(m.to)}
              onClick={() => { setMenuOpen(false); navigate({ to: m.to }) }}
            />
          ))}
        </SideNavSection>
        <SideNavSection title="Money">
          {MONEY_MENU.filter((m) => !m.adminOnly || isAdmin(me?.role)).map((m) => (
            <SideNavItem
              key={m.to}
              label={m.label}
              isSelected={path.startsWith(m.to)}
              onClick={() => { setMenuOpen(false); navigate({ to: m.to }) }}
            />
          ))}
        </SideNavSection>
        <SideNavSection title="This phone">
          {DEVICE_MENU.map((m) => (
            <SideNavItem
              key={m.to}
              label={m.label}
              isSelected={path.startsWith(m.to)}
              onClick={() => { setMenuOpen(false); navigate({ to: m.to }) }}
            />
          ))}
        </SideNavSection>
      </MobileNav>

      {adding && <AddExpenseSheet onClose={() => setAdding(false)} />}
    </Stack>
  )
}
